import React, { useState } from 'react';

import { faBolt, faClipboard, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faBolt as faBoltRegular, faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ActivityList from './activity_list';
import ActivityModal from 'bundles/apex/activities/modal';
import { Button } from 'components/ui/button';
import CopyOverlay from 'components/copy_overlay';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import Filters from 'bundles/apex/activities/filters';
import LoadingSpinner from 'components/ui/loading_spinner';
import { buildActivity } from 'bundles/apex/activities/form/helpers';
import { useActivitiesState } from 'bundles/apex/activities/hooks';
import * as filterHelpers from './filters/helpers';
import Pagination from 'components/ui/pagination';
import classNames from 'classnames';

import type { Activity, Features, FilterOptions, PaginationData } from 'bundles/apex/activities/types';
import type { ActivityFilters } from './filters/types';
import type { MenuOptions } from 'bundles/apex/activities/types';
import type { Parent, ParentOption } from 'bundles/crm/types';
import type { Paths } from 'bundles/apex/activities/hooks';

export interface ActivitiesProps {
  activities: Activity[];
  activityMenuOptions?: MenuOptions;
  importerProps: any;
  isBackofficeAdmin?: boolean;
  forUserId?: number;
  currentParent?: Parent | Parent[];
  features?: Features;
  options?: {
    parents?: ParentOption[];
  }
  filters?: boolean | ActivityFilters;
  filterOptions?: FilterOptions;
  header?: boolean;
  hideAfterReparenting?: boolean;
  inboxAddress?: string;
  listClassName?: string;
  paginate?: boolean;
  paths?: { availableTags?: string, create?: string, latest?: string } & Paths,
  showParents?: boolean | 'path';
  smallCreateButton?: boolean;
  tagText?: string;
  onChange?: (activities: Activity[]) => void;
  onEmptyStateClick?: () => void;
  onPageDataChange?: (pageData: PaginationData) => void;
}

export default function Activities({
  activities: activitiesProp,
  activityMenuOptions,
  currentParent,
  features,
  options,
  filters: showFilters,
  filterOptions,
  header,
  hideAfterReparenting,
  inboxAddress,
  listClassName,
  paginate,
  paths,
  showParents,
  smallCreateButton,
  forUserId,
  isBackofficeAdmin,
  tagText,
  onChange,
  onEmptyStateClick,
  onPageDataChange,
}: ActivitiesProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const {
    activities,
    loading,
    pageData,
    addActivity,
    removeActivity,
    replaceActivity,
    filters,
    changeFilters,
  } = useActivitiesState({ activities: activitiesProp, paginate, paths, onChange, onPageDataChange });
  const hasFilters = filterHelpers.hasFilters(filters);


  let onClick;
  if (onEmptyStateClick) {
    onClick = onEmptyStateClick;
  } else if (paths?.create) {
    onClick = () => setShowCreateModal(true);
  }

  return (
    <>
      {(activities.length === 0 && !loading && (!showFilters)) ? (
        <Empty className="bg-white">
          <Empty.Media>
            <FontAwesomeIcon icon={faBolt} />
          </Empty.Media>
          <Empty.Title>No Activities to Display</Empty.Title>
          <Empty.Content>Get started by adding an activity</Empty.Content>
          {onClick && (
            <Empty.Actions>
              <Button icon={faPlus} label="Add activity" onClick={onClick} />
            </Empty.Actions>
          )}
        </Empty>
      ) : (
        <div className="d-flex flex-column flex-grow-1 overflow-hidden gap-3">
          {header && (
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon className="me-2 text-primary" icon={faBoltRegular} size="lg" />
                <h6 className="fw-bold" key="header">Activities</h6>
                {inboxAddress && (
                  <CopyOverlay
                    className="text-muted"
                    dark
                    text={inboxAddress}
                    tooltip="Copy inbox address for this record"
                  >
                    <FontAwesomeIcon icon={faClipboard} />
                  </CopyOverlay>
                )}
              </div>
              {paths?.create && (
                smallCreateButton ? (
                  <Button
                    icon={faCirclePlus}
                    key="add"
                    label="Add Activity"
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCreateModal(true)}
                  />
                ) : (
                  <Button icon={faPlus} key="add" onClick={() => setShowCreateModal(true)}>
                    New activity
                  </Button>
                ))}
            </div>
          )}

          <Filters
            features={features}
            filterOptions={filterOptions}
            filters={filters}
            show={showFilters}
            onChange={changeFilters}
          />

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className={classNames(listClassName)}>
              <ActivityList
                activities={activities}
                activityMenuOptions={activityMenuOptions}
                currentParent={currentParent}
                forUserId={forUserId}
                hasFilters={hasFilters}
                hideAfterReparenting={hideAfterReparenting}
                isBackofficeAdmin={isBackofficeAdmin}
                options={options}
                showParents={showParents}
                tagText={tagText}
                onDelete={removeActivity}
                onSave={replaceActivity}
              />
            </div>
          )}

          {(!loading && pageData) &&
            <Pagination
              currentPage={pageData.currentPage}
              totalPages={pageData.totalPages}
              onChange={page => changeFilters({ page })}
            />
          }
        </div>
      )}

      <ActivityModal
        activity={buildActivity(paths?.create, { availableTagsPath: paths?.availableTags })}
        header="Add activity"
        options={options}
        show={showCreateModal}
        tagText={tagText}
        onHide={() => setShowCreateModal(false)}
        onSave={(activity) => {
          addActivity(activity);
          setShowCreateModal(false);
        }}
      />
    </>
  );
}
